'use client';

/**
 * Icon — einheitliches, eigenständig gezeichnetes Icon-System (Athlyn).
 * ─────────────────────────────────────────────────────────────
 * Premium-getunte Weiterentwicklung von `components/Icons.js`:
 *
 *   - Eine API: <Icon name="home" size={22} />  (statt 30 Einzel-Komponenten)
 *   - Outline-only, currentColor → erbt die Farbe vom Container.
 *   - Stroke 1.75 (feiner/edler als der alte 2er), round cap + join.
 *   - ViewBox 24×24, jedes Glyph auf gemeinsamem Grid → wirkt verwandt.
 *   - Liest sich identisch auf hellem Cream und auf dunklem Slate.
 *
 * App-weiter Austausch der Inline-SVGs läuft in Phase E; hier ist die
 * Single Source, gegen die migriert wird. Import via `shared/ui`.
 */

import React, { type ReactNode } from 'react';

export type IconName =
  | 'home'
  | 'food'
  | 'camera'
  | 'dumbbell'
  | 'settings'
  | 'flame'
  | 'sparkle'
  | 'droplet'
  | 'scale'
  | 'target'
  | 'clock'
  | 'calendar'
  | 'chevronLeft'
  | 'chevronRight'
  | 'plus'
  | 'check'
  | 'close'
  | 'bolt'
  | 'arrowUp'
  | 'trend'
  | 'meal'
  | 'user'
  | 'moon'
  | 'sun'
  | 'shield'
  | 'info'
  | 'pencil'
  | 'barcode'
  // ── Ziel-Icons (Onboarding-Ziel-Step, GoalEditModal, ProfileGoalCard) ──
  // Custom Single-Stroke-Glyphe pro Ziel; ersetzen die früheren OS-Emoji.
  | 'goal-lose'
  | 'goal-define'
  | 'goal-leanbulk'
  | 'goal-bulk'
  | 'goal-maintain'
  | 'goal-health';

export interface IconProps {
  name: IconName;
  /** Kantenlänge in px. Default 22. */
  size?: number;
  /** Override-Farbe. Default currentColor (erbt vom Container). */
  color?: string;
  /** Stroke-Stärke. Default 1.75 (premium-fein). */
  strokeWidth?: number;
  /** Zugänglichkeits-Label; ohne wird das Icon als dekorativ behandelt. */
  title?: string;
}

/** Pfad-Registry. `c` = aufgelöste Farbe (für die wenigen Fill-Punkte). */
const PATHS: Record<IconName, (c: string) => ReactNode> = {
  home: () => (
    <path d="M3 11 L 12 3 L 21 11 V 20 a 1 1 0 0 1 -1 1 h-5 V 14 h-4 V 21 H 4 a 1 1 0 0 1 -1 -1 Z" />
  ),
  food: () => (
    <>
      <path d="M6 3 V 8 a 2 2 0 0 0 4 0 V 3" />
      <path d="M8 8 V 21" />
      <path d="M16 3 C 18 5, 18 9, 16 11" />
      <path d="M16 11 V 21" />
    </>
  ),
  camera: () => (
    <>
      <path d="M3 8 H 7 L 9 5 H 15 L 17 8 H 21 a 1 1 0 0 1 1 1 V 19 a 1 1 0 0 1 -1 1 H 3 a 1 1 0 0 1 -1 -1 V 9 a 1 1 0 0 1 1 -1 Z" />
      <circle cx="12" cy="13" r="4" />
      <circle cx="12" cy="13" r="1.2" />
    </>
  ),
  dumbbell: () => (
    <>
      <path d="M3 10 V 14" />
      <path d="M5 7 V 17" />
      <path d="M7 9 V 15" />
      <path d="M7 12 H 17" />
      <path d="M17 9 V 15" />
      <path d="M19 7 V 17" />
      <path d="M21 10 V 14" />
    </>
  ),
  settings: () => (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2 V 5" />
      <path d="M12 19 V 22" />
      <path d="M22 12 H 19" />
      <path d="M5 12 H 2" />
      <path d="M19.07 4.93 L 16.95 7.05" />
      <path d="M7.05 16.95 L 4.93 19.07" />
      <path d="M19.07 19.07 L 16.95 16.95" />
      <path d="M7.05 7.05 L 4.93 4.93" />
    </>
  ),
  flame: () => (
    <>
      <path d="M11 3 C 9 7, 6.5 10, 6.5 14.5 C 6.5 18.5, 9 21, 12 21 C 15 21, 17.5 18.5, 17.5 14.5 C 17.5 11, 15.5 9, 14 5.5" />
      <path d="M12 13 C 11 14.5, 10.5 15.5, 10.5 16.5 C 10.5 17.5, 11.2 18, 12 18 C 12.8 18, 13.5 17.5, 13.5 16.5 C 13.5 15.5, 13 14.5, 12 13" />
    </>
  ),
  sparkle: () => (
    <>
      <path d="M12 4 L 13.5 10.5 L 20 12 L 13.5 13.5 L 12 20 L 10.5 13.5 L 4 12 L 10.5 10.5 Z" />
      <path d="M18 4 L 18.5 6 L 20.5 6.5 L 18.5 7 L 18 9 L 17.5 7 L 15.5 6.5 L 17.5 6 Z" />
    </>
  ),
  droplet: () => (
    <path d="M12 3 C 10 6, 6 10, 6 14 a 6 6 0 0 0 12 0 C 18 10, 14 6, 12 3 Z" />
  ),
  scale: c => (
    <>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M7 10 H 17" />
      <path d="M12 10 V 16" />
      <circle cx="12" cy="14" r="0.8" fill={c} stroke="none" />
    </>
  ),
  target: c => (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.2" fill={c} stroke="none" />
    </>
  ),
  clock: () => (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7 V 12 L 15.5 14" />
    </>
  ),
  calendar: () => (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10 H 21" />
      <path d="M8 3 V 7" />
      <path d="M16 3 V 7" />
    </>
  ),
  chevronLeft: () => <path d="M15 6 L 9 12 L 15 18" />,
  chevronRight: () => <path d="M9 6 L 15 12 L 9 18" />,
  plus: () => (
    <>
      <path d="M12 5 V 19" />
      <path d="M5 12 H 19" />
    </>
  ),
  check: () => <path d="M4 12 L 9 17 L 20 6" />,
  close: () => (
    <>
      <path d="M6 6 L 18 18" />
      <path d="M18 6 L 6 18" />
    </>
  ),
  bolt: () => <path d="M13 3 L 4 13 H 11 L 10 21 L 20 11 H 13 L 14 3 Z" />,
  arrowUp: () => (
    <>
      <path d="M12 19 V 5" />
      <path d="M5 12 L 12 5 L 19 12" />
    </>
  ),
  trend: c => (
    <>
      <path d="M3 17 L 9 11 L 13 15 L 21 7" />
      <path d="M15 7 H 21 V 13" />
      <circle cx="9" cy="11" r="0.9" fill={c} stroke="none" />
      <circle cx="13" cy="15" r="0.9" fill={c} stroke="none" />
    </>
  ),
  meal: () => (
    <>
      <ellipse cx="12" cy="16" rx="9" ry="3.5" />
      <path d="M3 16 V 9 C 3 11, 7 12.5, 12 12.5 S 21 11, 21 9 V 16" />
      <path d="M8 7 C 8 8, 10 9, 12 9 S 16 8, 16 7" />
    </>
  ),
  user: () => (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21 V 20 a 6 6 0 0 1 6 -6 h 4 a 6 6 0 0 1 6 6 v 1" />
    </>
  ),
  moon: () => <path d="M20 14 a 8 8 0 1 1 -10 -10 a 6 6 0 0 0 10 10 Z" />,
  sun: () => (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5 V 5 M12 19 V 21.5 M2.5 12 H 5 M19 12 H 21.5 M5.2 5.2 L 6.9 6.9 M17.1 17.1 L 18.8 18.8 M18.8 5.2 L 17.1 6.9 M6.9 17.1 L 5.2 18.8" />
    </>
  ),
  shield: () => (
    <>
      <path d="M12 2 L 4 6 V 12 C 4 17, 8 21, 12 22 C 16 21, 20 17, 20 12 V 6 Z" />
      <path d="M9 12 L 11 14 L 15 10" />
    </>
  ),
  info: () => (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11 V 17" />
      <path d="M12 8 V 8.1" />
    </>
  ),
  pencil: () => (
    // Stift — Manuell/Bearbeiten (ersetzt ✏️)
    <>
      <path d="M4 16 L 4 20 L 8 20 L 18.5 9.5 a 2.12 2.12 0 0 0 -3 -3 Z" />
      <path d="M13.5 6.5 L 17.5 10.5" />
    </>
  ),
  barcode: () => (
    // Barcode — Barcode-Scan/Quelle (ersetzt 📷/▦)
    <>
      <path d="M4 6 V 18" />
      <path d="M7 6 V 18" />
      <path d="M10 6 V 18" />
      <path d="M13.5 6 V 18" />
      <path d="M17 6 V 18" />
      <path d="M20 6 V 18" />
    </>
  ),

  // ── Ziel-Icons ──────────────────────────────────────────────────────────
  // Alle auf demselben 24er-Grid, Single-Stroke, currentColor (Lime im UI).
  'goal-lose': () => (
    // Flamme — Abnehmen/Fettverlust
    <>
      <path d="M12 2 C 12.5 6, 15.5 7.5, 15.5 12.5 A 4 4 0 0 1 7.5 12.5 C 7.5 9.8, 9 8.8, 9.5 7 C 10 8, 11 8, 11.5 6 C 11.7 5, 12 4, 12 2 Z" />
      <path d="M12 14 C 11.3 15, 11 15.7, 11 16.5 A 1.2 1.2 0 0 0 13 16.5 C 13 15.7, 12.7 15, 12 14 Z" />
    </>
  ),
  'goal-define': () => (
    // Blitz/Funke — Definieren
    <path d="M13 2 L 4 14 H 10.5 L 10 22 L 20 9 H 13.5 L 13 2 Z" />
  ),
  'goal-leanbulk': () => (
    // Angespannter Arm — sauber aufbauen
    <>
      <path d="M6 21 V 13 C 6 9.5, 8.5 7, 12 7 H 14.5" />
      <path d="M14.5 7 C 17.5 7, 19.5 9, 19.5 12 C 19.5 14.8, 17.3 16.5, 14.5 16.5 C 12.3 16.5, 11 15, 10.5 13.2" />
      <path d="M6 13.5 C 8 12.3, 9.6 13, 10.7 15" />
    </>
  ),
  'goal-bulk': () => (
    // Hantel — Muskeln aufbauen
    <>
      <path d="M4 9 V 15" />
      <path d="M6.5 6.5 V 17.5" />
      <path d="M6.5 12 H 17.5" />
      <path d="M17.5 6.5 V 17.5" />
      <path d="M20 9 V 15" />
    </>
  ),
  'goal-maintain': () => (
    // Waage/Balance — Gewicht halten
    <>
      <path d="M12 4 V 19" />
      <path d="M7 19 H 17" />
      <path d="M5 7 H 19" />
      <path d="M5 7 L 3 12 H 7 Z" />
      <path d="M19 7 L 17 12 H 21 Z" />
    </>
  ),
  'goal-health': () => (
    // Blatt — Gesundheit
    <>
      <path d="M20 4 C 10 4, 4 11, 4 20 C 13 20, 20 13, 20 4 Z" />
      <path d="M6 18 C 10 14, 13 11, 17 8" />
    </>
  ),
};

export function Icon({
  name,
  size = 22,
  color = 'currentColor',
  strokeWidth = 1.75,
  title,
}: IconProps) {
  const render = PATHS[name];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {render(color)}
    </svg>
  );
}
